import PageBackTitle from "../Component/PageBackTitle";
import React, { useEffect, useRef, useState } from "react";
import "../forms/UserForm.css";
import "../forms/EquipmentForm.css";
import "../forms/AmcSchedules.css";
import "../SafetyObservation/safetyObservation.css";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import API_URL from "../../src/Config";
import ObservationFormFields from "../SafetyObservation/ObservationFormFields";
import {
  buildObservationFormData,
  fetchDepartmentOptions,
  isExistingObservationAttachment,
  normalizeDateToYyyyMmDd,
  parseApiError,
} from "../SafetyObservation/observationUtils";

const INITIAL_FORM_DATA = {
  title: "",
  type: "",
  severity: "",
  description: "",
  location: "",
  observer_name: "",
  department_id: "",
  observed_date: "",
};

function EditSafetyObservation({ isSidebarExpanded }) {
  const navigate = useNavigate();
  const { observationId } = useParams();
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [errors, setErrors] = useState({});
  const [departments, setDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [isDocumentPopupOpen, setIsDocumentPopupOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);

  const showSnackbar = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = (_, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  const validateForm = () => {
    const validationErrors = {};

    if (!formData.title.trim()) validationErrors.title = "Title is required.";
    if (!formData.type) validationErrors.type = "Type is required.";
    if (!formData.severity) validationErrors.severity = "Severity is required.";
    if (!formData.observed_date)
      validationErrors.observed_date = "Observed date is required.";

    return validationErrors;
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;

    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));

    setErrors((prevState) => ({
      ...prevState,
      [name]: "",
    }));
  };

  const handleDepartmentChange = (selectedOption) => {
    setSelectedDepartment(selectedOption);
    setFormData((prevState) => ({
      ...prevState,
      department_id: selectedOption ? selectedOption.value : "",
    }));
  };

  const handleCancelClick = () => {
    navigate("/safety-observations");
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileInputChange = (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    if (selectedFiles.length === 0) return;

    setDocuments((prevState) => [...prevState, ...selectedFiles]);
    setErrors((prevState) => ({
      ...prevState,
      documents: "",
    }));
    event.target.value = "";
  };

  const handleDeleteDocument = (index) => {
    setDocuments((prevState) => {
      const targetDocument = prevState[index];

      if (isExistingObservationAttachment(targetDocument)) {
        return prevState;
      }

      return prevState.filter((_, currentIndex) => currentIndex !== index);
    });
  };

  const handleSaveClick = async () => {
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      setIsLoading(true);
      const authKey = localStorage.getItem("authKey");
      const payload = buildObservationFormData(
        {
          ...formData,
          department_id: formData.department_id || "",
          observed_date: normalizeDateToYyyyMmDd(formData.observed_date) || "",
          description: formData.description || "",
          location: formData.location || "",
        },
        documents,
      );

      const response = await axios.put(
        `${API_URL}/api/safety/observations/${observationId}`,
        payload,
        {
          headers: {
            Authorization: authKey,
            "Content-Type": "multipart/form-data",
          },
        },
      );

      showSnackbar(
        response.data?.message || "Observation updated successfully",
      );
      window.setTimeout(() => {
        navigate("/safety-observations");
      }, 900);
    } catch (error) {
      showSnackbar(
        parseApiError(error, "Failed to update observation"),
        "error",
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const loadPage = async () => {
      try {
        setIsFetching(true);
        const authKey = localStorage.getItem("authKey");

        const [departmentOptions, observationResponse] = await Promise.all([
          fetchDepartmentOptions(),
          axios.get(`${API_URL}/api/safety/observations/${observationId}`, {
            headers: {
              Authorization: authKey,
            },
          }),
        ]);

        const observation = observationResponse.data || {};
        setDepartments(departmentOptions);
        setFormData({
          title: observation.title || "",
          type: observation.type || "",
          severity: observation.severity || "",
          description: observation.description || "",
          location: observation.location || "",
          observer_name: observation.observer_name || "",
          department_id: observation.department_id || "",
          observed_date: normalizeDateToYyyyMmDd(observation.observed_date),
        });
        setDocuments(
          Array.isArray(observation.attachments) ? observation.attachments : [],
        );

        if (observation.department_id) {
          const matchedDepartment = departmentOptions.find(
            (option) => option.value === observation.department_id,
          );

          setSelectedDepartment(
            matchedDepartment || {
              value: observation.department_id,
              label: observation.department_name || "Selected Department",
            },
          );
        }
      } catch (error) {
        showSnackbar(
          parseApiError(error, "Failed to fetch observation"),
          "error",
        );
      } finally {
        setIsFetching(false);
      }
    };

    loadPage();
  }, [observationId]);

  return (
    <>
      <div
        className={`data-form-content-container page-content ${
          isSidebarExpanded ? "expanded" : "collapsed"
        }`}
      >
        <div className="data-form-content observation-form-wrapper">
          <div className="data-form-header">
            <div className="data-form-header-content">
              <PageBackTitle className="data-form-heading-content-left-side">Edit Observation</PageBackTitle>
            </div>
          </div>
          <div className="header-separator"></div>
          <div className="data-form-body">
            {isFetching ? (
              <div className="loader-container">
                <div className="loader"></div>
                <div>Loading...</div>
              </div>
            ) : (
              <form className="form-detail">
                <ObservationFormFields
                  formData={formData}
                  errors={errors}
                  departments={departments}
                  selectedDepartment={selectedDepartment}
                  onInputChange={handleInputChange}
                  onDepartmentChange={handleDepartmentChange}
                  fileInputRef={fileInputRef}
                  documents={documents}
                  isDocumentPopupOpen={isDocumentPopupOpen}
                  onUploadClick={handleUploadClick}
                  onFileInputChange={handleFileInputChange}
                  onToggleDocumentPopup={() =>
                    setIsDocumentPopupOpen((prevState) => !prevState)
                  }
                  onCloseDocumentPopup={() => setIsDocumentPopupOpen(false)}
                  onDeleteDocument={handleDeleteDocument}
                  isDocumentRemovable={(document) =>
                    !isExistingObservationAttachment(document)
                  }
                />
              </form>
            )}
          </div>
          <div className="footer">
            <div className="button-group">
              <button
                className="data-form-save-button"
                type="button"
                onClick={handleSaveClick}
                disabled={isLoading || isFetching}
              >
                {isLoading ? "Updating..." : "Update"}
              </button>
              <button
                className="data-form-cancel-button"
                type="button"
                onClick={handleCancelClick}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        style={{ marginTop: "35px", marginLeft: "20px" }}
      >
        <MuiAlert onClose={handleSnackbarClose} severity={snackbarSeverity}>
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </>
  );
}

export default EditSafetyObservation;
